/**
 * Credit-transactions ledger API wrapper (v2 — Transaction Ledger, DNKADM-0149 U1).
 *
 * The **credit-moved** ledger: `GET /v2/admin/credit-transactions` is the paged,
 * newest-first history of every credit movement (booking debits, refunds, grants,
 * expiries, admin adjustments, …) — role-gated OWNER/ADMIN, tenant-from-JWT,
 * bearer-only. It is the credit counterpart to the payment ledger
 * (`api/paymentsTransactions.ts`); together they are the Transaction Ledger, distinct
 * from the Activity log (action history).
 *
 * CONTRACT (dink-api handoff — DNKAPI-0280 credit-ledger, on `main`/in delivery).
 * `delta` is **credits** (`DECIMAL(8,2)` — negative = debit, positive = grant) —
 * `formatCredits` renders it. Credits are bucket-ID-keyed (`creditBucketId`, the 0052
 * bucket id), matching `api/members.ts`.
 *
 * **Forward-compat link field (DNKAPI-0281, landing shortly):** each row will
 * additionally carry a best-effort **`reservationUuid`** (null when unresolvable),
 * parsed NULL-SAFE now — present → value, absent → `null` — for the reservation
 * deep-link; until then the row shows `reservationId` as plain text.
 *
 * **Forward-compat location fields (DNKAPI-0283):** rows may also carry
 * `locationId` and best-effort `locationName`, both parsed NULL-SAFE. Until that
 * backend enrichment lands, absent fields stay `null`.
 *
 * **Enrichment is best-effort:** `contactName`/`bucketName` may be null (the raw
 * `contactId`/`creditBucketId` is the fallback) — a null name is NEVER an error.
 *
 * Paging is **0-INDEXED on the wire** (first page = `page=0`). The UI keeps a
 * 1-indexed page; `toWirePage`/`toUiPage` (`api/paging.ts`, DNKADM-0148) are the one
 * canonical conversion.
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049): bearer
 * token, NO CSRF, NO cookies, the bounded 401→refresh→retry.
 *
 * DEFENSIVE PARSE — mirrors `api/configAudit.ts` / `api/members.ts`: each scalar
 * degrades to `null`, `delta` is a finite number (defaults 0), a row with no `id` is
 * dropped, and a non-array payload yields an empty feed.
 */
import { request } from './client';
import { toWirePage, toUiPage } from './paging';
import { asPlainDay, LOT_DAY_WIRE } from './creditLots';

/**
 * The credit-movement reason (DNKAPI-0280). Carried as a plain string so an
 * unrecognized upstream reason still renders; the known set documents the contract
 * for the reason filter.
 */
export type CreditTransactionReason =
  | 'booking_debit'
  | 'cancel_refund'
  | 'monthly_refill'
  | 'package_grant'
  | 'standalone_purchase'
  | 'admin_adjust'
  | 'admin_lot_reduce'
  | 'expiry'
  | 'expiry_change'
  | (string & {});

/** The known credit reasons, in a stable display/filter order. */
export const CREDIT_TRANSACTION_REASONS: readonly CreditTransactionReason[] = [
  'booking_debit',
  'cancel_refund',
  'monthly_refill',
  'package_grant',
  'standalone_purchase',
  'admin_adjust',
  'admin_lot_reduce',
  'expiry',
  'expiry_change',
];

/**
 * One parsed credit-transaction row. Every enrichment/optional field may be `null`
 * on the wire; `delta` is a finite number (defaults 0); `reservationUuid` is the
 * DNKAPI-0281 forward-compat link field (present → value, absent → `null`);
 * `locationId`/`locationName` are DNKAPI-0283 forward-compat fields.
 */
export interface AdminCreditTransactionRow {
  /** The transaction id — the row key. A row with no id is dropped (never null here). */
  id: string;
  /** The member's external contact id — DATA, never surfaced as "GHL …". */
  contactId: string | null;
  /** Server-enriched contact name (best-effort), or null (fall back to `contactId`). */
  contactName: string | null;
  /** Server-enriched contact email (best-effort), or null. */
  contactEmail: string | null;
  /** Credits moved (`formatCredits`; negative = debit, positive = grant). Defaults 0. */
  delta: number;
  /** The 0052 credit-bucket id the movement applied to, or null. */
  creditBucketId: string | null;
  /** Server-enriched bucket name (best-effort), or null (fall back to `creditBucketId`). */
  bucketName: string | null;
  /** `booking_debit | cancel_refund | …`, or the raw string when unrecognized. */
  reason: CreditTransactionReason | null;
  /**
   * The expiry BOUNDARY of the credit lot this movement touched (DNKAPI-0550 U3 —
   * `@Nullable Instant lotExpiresAt`), as an ISO-8601 UTC string, or null.
   *
   * **`null` conflates THREE distinct meanings** and the row carries nothing that
   * separates them (`lotId` was removed here — see DNKADM-0395 U3):
   *  1. the row is **bucket-level** — a bucket adjustment that touched no lot;
   *  2. the lot **never expires** (`v2_credit_lot.expires_at IS NULL`);
   *  3. the lot can no longer be resolved (deleted / purged).
   *
   * So a null may ONLY be rendered as an **omission**. It must never become a
   * positive claim — in particular never "Never expires", which is true of case 2
   * alone and would be a falsehood on a bucket-level row. Note that
   * {@link expiryLabel} returns exactly that string for `null`, so call it only on
   * a non-null value.
   *
   * Like every lot boundary this is EXCLUSIVE (the first dead day), so the last
   * usable day is the day before — `expiryLabel` owns that convention.
   */
  lotExpiresAt: string | null;
  /** DNKAPI-0648 plain tenant-calendar last usable day of the lot, or null (older API / none). */
  lotLastUsableDay: string | null;
  /** The reservation id this movement relates to (plain text link target), or null. */
  reservationId: string | null;
  /**
   * Best-effort reservation UUID (DNKAPI-0281, forward-compat). Present → the value
   * for the `/reservations?reservation=<uuid>` deep link; absent/unresolvable → null
   * (the row shows `reservationId` as plain text instead).
   */
  reservationUuid: string | null;
  /** The owned v2 location id for this movement (DNKAPI-0283), or null when absent. */
  locationId: string | null;
  /** Server-enriched location name (best-effort, DNKAPI-0283), or null. */
  locationName: string | null;
  /** The admin/system actor that caused the movement, or null. */
  actor: string | null;
  /** A free-text note recorded with the movement, or null. */
  note: string | null;
  /** ISO `createdAt` (WHEN) — newest-first sort + display, or null. */
  createdAt: string | null;
}

/** Parsed `GET /v2/admin/credit-transactions` page (standard Spring `Page` envelope). */
export interface CreditTransactionsPage {
  items: AdminCreditTransactionRow[];
  page: number;
  size: number;
  totalElements: number | null;
  totalPages: number | null;
}

// --- Defensive parsing helpers (mirror api/configAudit.ts) ---------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 entity fk ids may
 * arrive as numbers and are normalized to the `string` the UI keys off. Used for
 * nested entity-reference ids (`creditBucketId`, `reservationId`); the
 * member's OWN identity (`contactId`) stays {@link asText} — never coerced.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A finite number (accepts numeric strings, e.g. a BigDecimal serialized oddly), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Parse one raw credit-transaction row DEFENSIVELY. Returns `null` for a row with NO
 * usable `id` (the caller drops it). `delta` is coerced to a finite number (defaults
 * `0`); every other field degrades to `null` rather than throwing.
 */
function parseCreditTransaction(raw: unknown): AdminCreditTransactionRow | null {
  const o = isObject(raw) ? raw : {};
  const id = asId(o['id']) ?? asId(o['transactionId']);
  if (id === null) return null; // no id ⇒ not renderable — drop it
  return {
    id,
    // The contact's OWN identity is a genuine external id — asText, never coerced.
    contactId: asText(o['contactId']),
    contactName: asText(o['contactName']),
    contactEmail: asText(o['contactEmail']),
    delta: asNumber(o['delta']) ?? 0,
    creditBucketId: asId(o['creditBucketId']) ?? asId(o['bucketId']),
    bucketName: asText(o['bucketName']),
    reason: asText(o['reason']),
    // DNKAPI-0550 U3. An ISO instant, parsed like `createdAt`; absent/odd → null,
    // which the table renders as an OMISSION only (see the field's doc comment).
    lotExpiresAt: asText(o['lotExpiresAt']),
    lotLastUsableDay: asPlainDay(o[LOT_DAY_WIRE.ledgerRead]),
    reservationId: asId(o['reservationId']),
    // DNKAPI-0281 forward-compat: present → value, absent → null (never throws).
    reservationUuid: asText(o['reservationUuid']),
    // DNKAPI-0283 forward-compat: present → value, absent → null (never throws).
    locationId: asId(o['locationId']),
    locationName: asText(o['locationName']),
    actor: asText(o['actor']),
    note: asText(o['note']),
    createdAt: asText(o['createdAt']),
  };
}

/**
 * Extract the rows array defensively — the live Spring `Page` publishes rows under
 * `content`; `items`/`results`/`data` are tolerated as fallbacks; a bare array is
 * accepted. A non-array (or missing) field yields an empty list; rows with no `id`
 * are dropped.
 */
function parseCreditTransactions(body: Record<string, unknown>): AdminCreditTransactionRow[] {
  const candidates = [body['content'], body['items'], body['results'], body['data']];
  const arr =
    candidates.find((c): c is unknown[] => Array.isArray(c)) ??
    (Array.isArray(body) ? (body as unknown[]) : null);
  if (!arr) return [];
  return arr
    .map(parseCreditTransaction)
    .filter((r): r is AdminCreditTransactionRow => r !== null);
}

/** Parse an epoch-or-ISO timestamp to a sortable number; unknown → -Infinity (sorts last). */
function timestampOrder(ts: string | null): number {
  if (ts === null) return Number.NEGATIVE_INFINITY;
  const ms = Date.parse(ts);
  if (Number.isFinite(ms)) return ms;
  const num = Number(ts);
  return Number.isFinite(num) ? num : Number.NEGATIVE_INFINITY;
}

/**
 * Re-sort newest-first by `createdAt` (mirrors configAudit) — defensive against a
 * server that returns a different order. Rows with no parseable timestamp sort to the
 * end, preserving their relative order (stable).
 */
function sortNewestFirst(rows: AdminCreditTransactionRow[]): AdminCreditTransactionRow[] {
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const diff = timestampOrder(b.r.createdAt) - timestampOrder(a.r.createdAt);
      return diff !== 0 ? diff : a.i - b.i;
    })
    .map(({ r }) => r);
}

function parsePage(
  body: unknown,
  fallbackPage: number,
  fallbackSize: number,
): CreditTransactionsPage {
  const o = isObject(body) ? body : {};
  return {
    items: sortNewestFirst(parseCreditTransactions(o)),
    page: asNumber(o['page']) ?? fallbackPage,
    size: asNumber(o['size']) ?? asNumber(o['pageLimit']) ?? fallbackSize,
    totalElements: asNumber(o['totalElements']) ?? asNumber(o['total']),
    totalPages: asNumber(o['totalPages']),
  };
}

// --- Endpoints -----------------------------------------------------------------

export interface ListCreditTransactionsParams {
  /** 1-indexed UI page; converted to the 0-indexed wire page. Defaults 1. */
  page?: number;
  /** Page size; defaults 20. */
  size?: number;
  /** Member filter (external contact id); sent as `?contactId=` ONLY when set. */
  contactId?: string;
  /** Bucket filter (0052 bucket id); sent as `?creditBucketId=` ONLY when set. */
  creditBucketId?: string;
  /** Reason filter; sent as `?reason=` ONLY when set. */
  reason?: CreditTransactionReason | string;
  /** Inclusive lower bound (`yyyy-MM-dd`); sent as `?from=` ONLY when set. */
  from?: string;
  /** Inclusive upper bound (`yyyy-MM-dd`); sent as `?to=` ONLY when set. */
  to?: string;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;

/**
 * `GET /v2/admin/credit-transactions?page&size[&contactId=&creditBucketId=&reason=&from=&to=]`
 * → a parsed, newest-first page of the credit-moved ledger. Defaults `page=1`,
 * `size=20`. Each filter is sent ONLY when set (omitting a filter widens the feed).
 * The 1-indexed UI page is sent as `toWirePage(page)` so the first UI page (1) maps
 * to wire `?page=0` (DNKADM-0148); the returned 0-indexed page maps back via
 * `toUiPage`. Bearer-only `/v2/admin/**` (no CSRF, no cookies). Throws `ApiError` on
 * a non-2xx response.
 */
export async function listCreditTransactions(
  params: ListCreditTransactionsParams = {},
): Promise<CreditTransactionsPage> {
  const page = params.page ?? DEFAULT_PAGE;
  const size = params.size ?? DEFAULT_PAGE_SIZE;
  const contactId = asText(params.contactId) ?? undefined;
  const creditBucketId = asText(params.creditBucketId) ?? undefined;
  const reason = asText(params.reason) ?? undefined;
  const from = asText(params.from) ?? undefined;
  const to = asText(params.to) ?? undefined;
  const body = await request<unknown>('GET', '/v2/admin/credit-transactions', {
    query: { page: toWirePage(page), size, contactId, creditBucketId, reason, from, to },
  });
  const parsed = parsePage(body, toWirePage(page), size);
  // Remap the backend's 0-indexed page back to the 1-indexed UI page (mirrors listConfigAudit).
  return { ...parsed, page: toUiPage(parsed.page) };
}
